import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from './ui/select';

const INITIAL_FORM = {
  name: '',
  address: '',
  description: '',
  bedrooms: 0,
  bathrooms: 0,
  property_type: '',
};

// Radix Select does not allow an empty-string item value, so we use a
// sentinel for the "No type" option and translate it back to '' in state.
const NO_TYPE_VALUE = '__none__';

const PropertyModal = ({
  isOpen,
  onClose,
  onSave,
  property,
  saving,
  saveError,
  onClearSaveError,
}) => {
  const [formData, setFormData] = useState(INITIAL_FORM);

  const isEditing = !!property;

  useEffect(() => {
    if (property) {
      setFormData({
        name: property.name || '',
        address: property.address || '',
        description: property.description || '',
        bedrooms: property.bedrooms ?? 0,
        bathrooms: property.bathrooms ?? 0,
        property_type: property.property_type || '',
      });
    } else {
      setFormData(INITIAL_FORM);
    }
  }, [property, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      name: formData.name,
      address: formData.address,
      description: formData.description,
      bedrooms: formData.bedrooms,
      bathrooms: formData.bathrooms,
      property_type: formData.property_type,
    });
  };

  const update = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (saveError && onClearSaveError) onClearSaveError();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Property' : 'Add New Property'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the details for this property.'
              : 'Fill in the details to add a new property.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 pt-2">
          {saveError && (
            <div
              role="alert"
              className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{saveError}</span>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="prop-name">Property Name</Label>
            <Input
              id="prop-name"
              required
              value={formData.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="e.g., Sunset Villa"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="prop-address">Address</Label>
            <Input
              id="prop-address"
              required
              value={formData.address}
              onChange={(e) => update('address', e.target.value)}
              placeholder="e.g., 123 Main St, Ames, IA 50014"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="prop-description">Description</Label>
            <Input
              id="prop-description"
              value={formData.description}
              onChange={(e) => update('description', e.target.value)}
              placeholder="Brief description of the property"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="prop-beds">Bedrooms</Label>
              <Input
                id="prop-beds"
                type="number"
                min="0"
                value={formData.bedrooms}
                onChange={(e) => update('bedrooms', parseInt(e.target.value) || 0)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="prop-baths">Bathrooms</Label>
              <Input
                id="prop-baths"
                type="number"
                min="0"
                value={formData.bathrooms}
                onChange={(e) => update('bathrooms', parseInt(e.target.value) || 0)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="prop-type">Type</Label>
              <Select
                value={formData.property_type || NO_TYPE_VALUE}
                onValueChange={(val) =>
                  update('property_type', val === NO_TYPE_VALUE ? '' : val)
                }
              >
                <SelectTrigger id="prop-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_TYPE_VALUE}>None</SelectItem>
                  <SelectItem value="apartment">Apartment</SelectItem>
                  <SelectItem value="house">House</SelectItem>
                  <SelectItem value="ranch">Ranch</SelectItem>
                  <SelectItem value="condo">Condo</SelectItem>
                  <SelectItem value="townhouse">Townhouse</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#16A34A] hover:bg-[#15803D] text-white"
            >
              {saving
                ? 'Saving…'
                : isEditing
                ? 'Save Changes'
                : 'Add Property'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PropertyModal;
